<template>
    <div class="chat" :class="{['expanded']: expanded }">
        <div class="chat-title" @click="toggle">
            <span class="title-text">
                Chat
            </span>
            <span class="chat-toggle">
                <fa-icon class="toggle-icon expand-icon" icon="chevron-up" v-if="!expanded" />
                <fa-icon class="toggle-icon collapse-icon" icon="chevron-down" v-if="expanded" />
            </span>
            <span class="unread-messages-count" v-if="unreadMessagesCount">
                {{ unreadMessagesCount > 9 ? '9+' : unreadMessagesCount }}
            </span>
        </div>
        <div class="chat-inner-wrapper">
            <div class="messages" ref="messages">
                <div class="message-wrapper" v-for="message in messages" :key="`message-${message.id}`">
                    <span class="username">
                        <span
                            class="label"
                            :class="{ [getLabelColor(message.username)]: true }"
                        >
                            {{ message.username }}
                        </span>:
                    </span>
                    <span class="message" v-html="message.message">
                    </span>
                </div>
            </div>
            <form class="chat-form" v-on:submit.prevent="onSubmit">
                <textarea
                    class="form-control chat-input"
                    v-model="message"
                    v-on:keypress.enter="onEnter"
                    placeholder="Send a message"
                    name="message"
                    autocomplete="off"
                    :rows="textareaRows"
                    @input="onInputChange"
                >
                </textarea>
                <button class="btn btn-primary submit-btn" @click="onEnter" v-if="message.length">
                    Send
                </button>
            </form>
        </div>
    </div>
</template>

<script>
import find from 'lodash.find';

export default {
    name: 'Chat',
    props: ['gameStatus'],
    async created() {
        await this.$store.dispatch('setColors', this.gameId);
        await this.$store.dispatch('setGameId', this.gameId);
        await this.$store.dispatch('setMessages', []);
        await this.$store.dispatch('setReadMessages', []);
        await this.$store.dispatch('getMessages', this.gameId);
        await this.$store.dispatch('setReadMessages', this.messages);
        this.scrollToBottom();
    },
    updated() {
        if (!this.loaded) {
            this.scrollToBottom();
            this.loaded = true;
        }
    },
    data() {
        return {
            message: '',
            textareaRows: 1,
            loaded: false,
        };
    },
    watch: {
        gameStatus(current, prev) {
            if (prev !== current) {
                this.scrollToBottom();
            }
        },
        showViaState(current, prev) {
            if (!prev && current) {
                this.toggle();
                this.$store.dispatch('setShowChat', false);
            }
        },
    },
    computed: {
        chatState() {
            return this.$store.state.chatState;
        },
        expanded() {
            return this.chatState.expanded;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        messages() {
            return this.chatState.messages;
        },
        readMessages() {
            return this.chatState.readMessages;
        },
        unreadMessagesCount() {
            return this.messages.length - this.readMessages.length;
        },
        ref() {
            return this.$refs.messages;
        },
        showViaState() {
            return this.chatState.show;
        },
    },
    methods: {
        onSubmit() {
            const { message, gameId } = this;

            if (message.trim()) {
                const options = {
                    message,
                    gameId,
                };

                this.$store.dispatch('sendMessage', options);

                this.message = '';
                this.textareaRows = 1;
            }
        },
        onEnter(event) {
            if (event.shiftKey) {
                return;
            }
            event.preventDefault();
            this.onSubmit();
        },
        onInputChange(event) {
            const message = event.target.value;
            this.message = message;
            let chars = message.length;
            const charsPerRow = 42;
            const lineBreaks = message.match(/\r|\n/) || [];

            if (lineBreaks.length) {
                chars += lineBreaks.length * charsPerRow;
            }

            if (chars < charsPerRow) {
                this.textareaRows = 1;
            } else if (chars >= charsPerRow && chars <= charsPerRow * 2) {
                this.textareaRows = 2;
            } else if (chars >= charsPerRow * 2 && chars <= charsPerRow * 3) {
                this.textareaRows = 3;
            } else {
                this.textareaRows = 4;
            }
        },
        async toggle() {
            const expandedState = !this.expanded;
            await this.$store.dispatch('setExpanded', expandedState);

            if (expandedState) {
                this.scrollToBottom();
            }
        },
        scrollToBottom() {
            this.ref.scrollTop = this.ref.scrollHeight;
        },
        getLabelColor(username) {
            const chatUsers = [...this.chatState.users];
            const userSettings = find(chatUsers, { username });

            if (userSettings) {
                return userSettings.color;
            }

            const colors = this.chatState.colors;
            const colorIndex = this.chatState.lastColorIndex < colors.length ? this.chatState.lastColorIndex : 0;
            const color = colors[colorIndex];

            chatUsers.push({
                username,
                color,
            });

            this.$store.dispatch('setLastColorIndex', colorIndex + 1);
            this.$store.dispatch('setChatUsers', chatUsers);

            return color;

        },
    },
    sockets: {
        async onUpdateChat(gameId) {
            if (this.gameId !== gameId) {
                return;
            }

            await this.$store.dispatch('getMessages', this.gameId);

            if (this.expanded) {
                this.scrollToBottom();
            }
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .chat {
        z-index: 200;
        background: white;
        border-radius: 4px;
        width: 115px;
        border: 1px solid $light-gray;
        font-family: 'Avenir', Helvetica, Arial, 'RobotoSlab', sans-serif;

        &.expanded {
            width: 100%;

            .chat-title {
                border-bottom: 1px solid $light-gray;
                padding-right: 10px;
            }

            .chat-inner-wrapper {
                display: block;
            }

            .chat-toggle {

                .toggle-icon {
                    vertical-align: middle;
                }
            }
        }

        .chat-inner-wrapper {
            display: none;
        }

        .chat-title {
            text-transform: uppercase;
            font-weight: bold;
            line-height: 1.5em;
            vertical-align: middle;
            padding: 8px 6px;
            color: $dark-brown;
            position: relative;
            padding-right: 20px;
            cursor: pointer;

            .title-text {
                font-family: 'IrishUncialfabeta';
                font-size: 1.3em;
                vertical-align: middle;
            }
        }

        .chat-toggle {
            cursor: pointer;
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);

            .toggle-icon {
                vertical-align: middle;
                font-size: 1.1em;

                &.expand-icon {
                    margin-bottom: 1px;
                }
            }
        }

        .messages {
            text-align: left;
            height: 150px;
            overflow: auto;
            margin: 10px auto;
            color: $dark-gray-alt;

            .message-wrapper {
                padding: .35em 1.5em;

                .username {
                    display: inline-block;

                    .label {
                        display: inline-block;
                        font-weight: bold;
                    }
                }

                .message {
					margin-left: 3px;
                    display: inline;
                    word-break: break-word;
                    white-space: pre-wrap;
                }
            }
        }

        .unread-messages-count {
            position: absolute;
            right: -10px;
            top: -12px;
            font-size: .85em;
            background: $teal;
            color: white;
            border-radius: 50%;
            width: 22px;
            height: 22px;
            vertical-align: middle;
            display: inline-block;
            padding: 2px;
            line-height: 22px;
        }

        .chat-form {
            padding: 12px;
            position: relative;

            .chat-input {
                background: $extra-light-gray;
                color: $dark-gray;
                font-weight: 300;
                border: 2px solid transparent;
                padding: 14px 80px 14px 12px;
                font-family: 'Avenir', Helvetica, Arial, 'RobotoSlab', sans-serif;
                resize: none;

                &:focus {
                    background: white;
                    border-color: $teal-alt;
                }
            }

            .submit-btn {
                font-size: .9em;
                min-height: 39px;
                position: absolute;
                right: 26px;
                top: 50%;
                transform: translateY(-50%);
            }
        }

        .username {

            .label {

                &.archer {
                    color: $archer;
                }

                &.berserker {
                    color: $berserker;
                }

                &.cavalry {
                    color: $cavalry;
                }

                &.crossbowman {
                    color: $crossbowman;
                }

                &.ensign {
                    color: $ensign;
                }

                &.footman {
                    color: $footman;
                }

                &.knight {
                    color: $knight;
                }

                &.lancer {
                    color: $lancer;
                }

                &.light-cavalry {
                    color: $light-cavalry;
                }

                &.marshal {
                    color: $marshal;
                }

                &.mercenary {
                    color: $mercenary;
                }

                &.pikeman {
                    color: $pikeman;
                }

                &.royal-guard {
                    color: $royal-guard;
                }

                &.scout {
                    color: $scout;
                }

                &.swordsman {
                    color: $swordsman;
                }

                &.warrior-priest {
                    color: $warrior-priest;
                }
            }
        }
    }

    .game-container {

        .chat {

            &:not(.expanded) {

                .chat-title {
                    display: none;
                }
            }
        }
    }

    @media only screen and (min-width: $screen-xs) {

        .game-container {

            .chat {
                z-index: 150;

                &.expanded {
                    width: 340px;
                }

                &:not(.expanded) {
                    .chat-title {
                        display: block;
                    }
                }

                .messages {
                    height: 200px;
                }

                .chat-form {
                    padding: 12px;

                    .chat-input {
                        padding-right: 12px;
                    }

                    .submit-btn {
                        display: none;
                    }
                }
            }
        }
    }
</style>
